import { GET_PRIZE_TEXT_GIFT_SPIN, GET_PRIZE_TEXT_MONEY } from '../../../constants';
import { buildPortalAssetUrl, formatNumberWithSpaces } from 'shared/utils/helpers';
import { campaignConfigRegistry } from '../configs';
import type { CampaignInfoType, CampaignLevel, CampaignSource, CampaignTypes, IPrizePopCampaign } from '../types/campaign.types';
import { CAMPAIGN_INFO_TYPES, CAMPAIGN_LEVELS, PRIZE_POP_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE } from '../types/campaign.types';
import { normalizeCampaignAssets } from './campaignAssetsMapper';

export const LEVEL_ORDER: Record<CampaignLevel, number> = {
  [CAMPAIGN_LEVELS.GOLD]: 1,
  [CAMPAIGN_LEVELS.PURPLE]: 2,
  [CAMPAIGN_LEVELS.RED]: 3,
  [CAMPAIGN_LEVELS.BLUE]: 4,
  [CAMPAIGN_LEVELS.GREEN]: 5,
  [CAMPAIGN_LEVELS.MADNESS]: 6,
};

export const isCampaign = (source: CampaignSource): source is IPrizePopCampaign => {
  return 'type' in source && source.type != null && source.type in campaignConfigRegistry;
};

/** Returns true if item belongs to the given campaign type */
export const isOfType = (source: CampaignSource, type: CampaignTypes): boolean => {
  return 'type' in source && source.type === type;
};

/**
 * Per-campaign-type contract describing which info types a card renders.
 *
 * This is the single source of truth for the "type knowledge" each variant owns:
 * the type cycled with the name in the collapsed header (`collapsedCycleType`)
 * and the types shown in the bottom info row (`bottomTypes`).
 */
export interface CampaignInfoContract {
  collapsedCycleType?: CampaignInfoType;
  bottomTypes: (hasMany: boolean, isJoined: boolean) => CampaignInfoType[];
}

const CAMPAIGN_INFO_CONTRACT: Partial<Record<CampaignTypes, CampaignInfoContract>> = {
  [TOURNAMENTS_CAMPAIGN_TYPE]: {
    collapsedCycleType: CAMPAIGN_INFO_TYPES.SCORE,
    bottomTypes: (hasMany: boolean, isJoined: boolean) => {
      if (!isJoined) return [CAMPAIGN_INFO_TYPES.TIMER];
      return hasMany
        ? [CAMPAIGN_INFO_TYPES.POSITION, CAMPAIGN_INFO_TYPES.TIMER]
        : [CAMPAIGN_INFO_TYPES.POSITION, CAMPAIGN_INFO_TYPES.SCORE, CAMPAIGN_INFO_TYPES.TIMER];
    },
  },
  [PRIZE_POP_CAMPAIGN_TYPE]: {
    collapsedCycleType: CAMPAIGN_INFO_TYPES.PRIZES,
    bottomTypes: (hasMany: boolean) =>
      hasMany
        ? [CAMPAIGN_INFO_TYPES.PRIZES, CAMPAIGN_INFO_TYPES.TIMER]
        : [CAMPAIGN_INFO_TYPES.PERIOD, CAMPAIGN_INFO_TYPES.PRIZES, CAMPAIGN_INFO_TYPES.TIMER],
  },
  [REBATE_CAMPAIGN_TYPE]: {
    // collapsedCycleType: Intentionally omitted, because Rebate cards deliberately don't cycle
    // an info type with the name in the collapsed header.
    bottomTypes: (hasMany: boolean, isJoined: boolean) => {
      if (!isJoined) return [CAMPAIGN_INFO_TYPES.TIMER];
      return hasMany
        ? [CAMPAIGN_INFO_TYPES.NEXT_WIN, CAMPAIGN_INFO_TYPES.TIMER]
        : [CAMPAIGN_INFO_TYPES.PROGRESS, CAMPAIGN_INFO_TYPES.NEXT_WIN, CAMPAIGN_INFO_TYPES.TIMER];
    },
  },
};

/** Resolve which info types to show on the bottom of a campaign card */
export const getCardInfoTypes = (
  campaignType: CampaignTypes | undefined,
  hasMany: boolean,
  isJoined: boolean,
): CampaignInfoType[] => {
  const contract = CAMPAIGN_INFO_CONTRACT[campaignType as CampaignTypes] ??
    CAMPAIGN_INFO_CONTRACT[PRIZE_POP_CAMPAIGN_TYPE];

  return contract?.bottomTypes(hasMany, isJoined) ?? [];
};

/** Resolve the info type cycled with the name in the collapsed header for a campaign type */
export const getCollapsedCycleType = (campaignType: CampaignTypes | undefined): CampaignInfoType | undefined => {
  return CAMPAIGN_INFO_CONTRACT[campaignType as CampaignTypes]?.collapsedCycleType;
};

export const getPrizeText = ({ type, name }: { type: string; name: string; }): string => {
  return [GET_PRIZE_TEXT_MONEY, GET_PRIZE_TEXT_GIFT_SPIN].includes(type) ? formatNumberWithSpaces(name) : name;
};


/** Build a CSS url() for the campaign timer SVG background by type */
export const getTimerBackgroundUrl = (campaignType: CampaignTypes): string => {
  const url = buildPortalAssetUrl(`/campaigns/icons/timerLobby_${campaignType}.svg`);
  return url ? `url(${url})` : '';
};

/** Resolve CSS color variables from the selected campaign's resources */
export const getSectionColors = (
  items: CampaignSource[],
  selectedCampaignId: string | null,
  campaignType: CampaignTypes,
): React.CSSProperties | undefined => {
  const campaign = selectedCampaignId
    ? items.find((c) => c.id === selectedCampaignId)
    : items[0];

  if (!campaign?.resources) return undefined;

  const assets = normalizeCampaignAssets(campaign.resources, campaignType);
  if (!assets.colors.fill && !assets.colors.border) return undefined;

  return {
    '--campaign-color-primary': assets.colors.border || assets.colors.fill,
    '--campaign-gradient-glow-box': assets.colors.fill
      ? `linear-gradient(to top, ${assets.colors.fill} 5%, transparent 60%)`
      : undefined,
    '--campaign-color-border': assets.colors.border || undefined,
    '--campaign-gradient-fill': assets.colors.fill || undefined,
    '--campaign-card-background': assets.colors.fill || undefined,
  } as React.CSSProperties;
};
